
import { Link } from 'react-router';
import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Car, Eye, Users, TrendingUp, Plus, ArrowDownRight, ArrowUpRight, Minus, AlertCircle, AlertTriangle, ChevronRight } from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from 'recharts';
import { getSellerOverview, SellerOverview } from '../../../services/dashboard.service';
import { Seo } from '../../components/Seo';
// ---- Helper Functions ----
const apiBaseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(/\/api\/v1\/?$/, '');
const getImageUrl = (vehicle: any) => {
  const image = Array.isArray(vehicle.Images) ? vehicle.Images[0] : null;
  if (!image) return 'https://placehold.co/120x80?text=Vehicle';
  return String(image).startsWith('http') ? image : `${apiBaseUrl}/volume/${String(image).replace(/^\/+/, '')}`;
};

const relativeTime = (value?: string, t?: any) => {
  if (!value) return t ? t('dashboard.time.recently') : 'Recently';
  const difference = Date.now() - new Date(value).getTime();
  const hours = Math.max(0, Math.floor(difference / 3_600_000));
  if (!t) {
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  if (hours < 1) return t('dashboard.time.justNow');
  if (hours < 24) return t('dashboard.time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('dashboard.time.daysAgo', { count: days });
};

// ---- Skeleton ----
const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function DashboardOverviewSkeleton() {
  return (
    <div className="p-4 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Skeleton className="h-8 w-40 mb-2" />
          <Skeleton className="h-4 w-44" />
        </div>
        <Skeleton className="h-11 w-40 rounded-xl" />
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 flex justify-between items-center mb-6">
        <Skeleton className="h-5 w-72" />
        <Skeleton className="h-4 w-20" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1,2,3,4].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
            <div className="flex justify-between mb-4">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <Skeleton className="h-4 w-16" />
            </div>
            <Skeleton className="h-8 w-24 mb-2" />
            <Skeleton className="h-4 w-28 mb-2" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
          <Skeleton className="h-6 w-36 mb-5" />
          <Skeleton className="h-[180px] w-full rounded-xl" />
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
          <Skeleton className="h-6 w-36 mb-5" />
          <Skeleton className="h-[180px] w-full rounded-xl" />
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-6">
        <div className="flex justify-between items-center mb-5">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="grid grid-cols-3 gap-4 mb-5">
          {[1,2,3].map(i => (
            <div key={i} className="border border-[#E5E7EB] rounded-xl p-4">
              <Skeleton className="h-8 w-10 mx-auto mb-2" />
              <Skeleton className="h-4 w-24 mx-auto" />
            </div>
          ))}
        </div>
        <Skeleton className="h-14 w-full rounded-xl" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
          <div className="flex justify-between p-5 border-b border-[#E5E7EB]">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-16" />
          </div>
          {[1,2,3].map(row => (
            <div key={row} className="flex gap-3 items-center p-4 border-b border-[#F3F4F6]">
              <Skeleton className="w-16 h-12 rounded-xl" />
              <div className="flex-1">
                <Skeleton className="h-4 w-40 mb-2" />
                <Skeleton className="h-3 w-24 mb-2" />
                <div className="flex gap-3">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
              <Skeleton className="h-6 w-16 rounded-lg" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
          <div className="flex justify-between p-5 border-b border-[#E5E7EB]">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-16" />
          </div>
          {[1,2,3].map(row => (
            <div key={row} className="flex gap-3 items-center p-4 border-b border-[#F3F4F6]">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <div className="flex-1">
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-3 w-24" />
              </div>
              <div>
                <Skeleton className="h-5 w-16 mb-2 rounded-lg" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---- Main Component ----
export function DashboardOverview() {
  const { t } = useTranslation('auth');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<SellerOverview | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadOverview = async () => {
      try {
        const data = await getSellerOverview();
        setOverview(data);
      } catch (requestError: any) {
        setError(requestError?.response?.data?.message || 'Unable to load the seller dashboard.');
      } finally {
        setLoading(false);
      }
    };
    loadOverview();
  }, []);

  if (loading) return <DashboardOverviewSkeleton />;
  if (error || !overview) {
    return (
      <div className="p-4 md:p-8">
        <div className="rounded-2xl border border-[#EF4444]/30 bg-[#EF4444]/5 p-6 text-center text-sm text-[#EF4444]">
          {error || 'No data available'}
        </div>
      </div>
    );
  }

  // Destructure with fallback defaults from backend SellerOverview shape
  const {
    kpis = {
      totalListings: 0,
      activeListings: 0,
      totalViews: 0,
      totalLeads: 0,
      listingsThisMonth: 0,
      listingsPreviousMonth: 0,
      leadsThisMonth: 0,
      leadsPreviousMonth: 0,
      viewsThisMonth: 0,
      viewsPreviousMonth: 0,
      profileViews: 0,
      profileViewsThisMonth: 0,
      profileViewsPreviousMonth: 0,
    },
    seller = { name: 'Seller' },
    recentListings = [],
    recentLeads = [],
    monthlyLeads = [],
    monthlyViews = [],
    complaints = { open: 0, pendingResponse: 0, resolved: 0, urgentComplaint: null },
  } = overview;

  // Trend helpers
  const getTrend = (current: number, previous: number) => {
    const difference = current - previous;
    if (difference > 0) return { text: `+${difference} ${t('dashboard.stats.thisMonth')}`, color: 'text-[#22C55E]', icon: ArrowUpRight };
    if (difference < 0) return { text: `${difference} ${t('dashboard.stats.vsLastMonth')}`, color: 'text-[#EF4444]', icon: ArrowDownRight };
    return { text: t('dashboard.stats.noChange'), color: 'text-[#717182]', icon: Minus };
  };

  const getPercentTrend = (current: number, previous: number) => {
    if (previous === 0) {
      if (current > 0) {
        return { text: `+100% ${t('dashboard.stats.thisMonth')}`, color: 'text-[#22C55E]', icon: ArrowUpRight };
      }
      return { text: t('dashboard.stats.noChange'), color: 'text-[#717182]', icon: Minus };
    }
    const diff = Math.round(((current - previous) / previous) * 100);
    if (diff > 0) {
      return { text: `+${diff}% ${t('dashboard.stats.thisMonth')}`, color: 'text-[#22C55E]', icon: ArrowUpRight };
    }
    if (diff < 0) {
      return { text: `${diff}% ${t('dashboard.stats.vsLastMonth')}`, color: 'text-[#EF4444]', icon: ArrowDownRight };
    }
    return { text: t('dashboard.stats.noChange'), color: 'text-[#717182]', icon: Minus };
  };

  const listingTrend = getTrend(kpis.listingsThisMonth, kpis.listingsPreviousMonth);
  const leadTrend = getTrend(kpis.leadsThisMonth, kpis.leadsPreviousMonth);
  const viewTrend = getPercentTrend(kpis.viewsThisMonth || 0, kpis.viewsPreviousMonth || 0);
  const profileViewsTrend = (kpis.profileViewsThisMonth || kpis.profileViewsPreviousMonth)
    ? getPercentTrend(kpis.profileViewsThisMonth || 0, kpis.profileViewsPreviousMonth || 0)
    : { text: t('dealerDashboard.kpi.vsLast', { percent: '0' }), color: 'text-[#22C55E]', icon: ArrowUpRight };

  // Complaints values
  const openComplaints = complaints.open || 0;
  const pendingComplaints = complaints.pendingResponse || 0;
  const resolvedComplaints = complaints.resolved || 0;
  const urgentComplaint = complaints.urgentComplaint || null;

  const stats = [
    { label: t('dashboard.stats.totalListings'), value: (kpis.totalListings || 0).toLocaleString(), trend: listingTrend, icon: Car, color: 'bg-[#1E6DFF]/10 text-[#1E6DFF]' },
    { label: t('dashboard.stats.totalViews'), value: (kpis.totalViews || 0).toLocaleString(), trend: viewTrend, icon: Eye, color: 'bg-[#22C55E]/10 text-[#22C55E]' },
    { label: t('dashboard.stats.leadsGenerated'), value: (kpis.totalLeads || 0).toLocaleString(), trend: leadTrend, icon: Users, color: 'bg-[#F59E0B]/10 text-[#F59E0B]' },
    { label: t('dashboard.stats.profileViews'), value: (kpis.profileViews || 0).toLocaleString(), trend: profileViewsTrend, icon: TrendingUp, color: 'bg-[#1E6DFF]/10 text-[#1E6DFF]' },
  ];

  return (
    <>
    <Seo
      title="Seller Dashboard | Morrocars"
      description="Manage your vehicle listings, track views and leads, monitor complaints, and view seller performance on Morrocars."
    />
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl text-[#2D323A]">{t('dashboard.title')}</h1>
          <p className="text-[#717182]">{t('dashboard.welcome', { name: seller?.name || 'Seller' })}</p>
        </div>
        <Link to="/dashboard/add-vehicle" className="flex items-center gap-2 px-5 py-2.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md">
          <Plus className="w-5 h-5" /> {t('dashboard.addVehicle')}
        </Link>
      </div>

      {/* Subscription Alert */}
      <div className="bg-[#1E6DFF]/5 border border-[#1E6DFF]/20 rounded-xl p-4 flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-[#1E6DFF]" />
          <span className="text-sm text-[#2D323A]">{t('dashboard.subscription.alert')}</span>
        </div>
        <Link to="/dashboard/subscription" className="text-sm text-[#1E6DFF] hover:underline">
          {t('dashboard.subscription.manage')} →
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((s) => {
          const Icon = s.icon;
          const TrendIcon = s.trend?.icon;
          return (
            <div key={s.label} className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${s.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-2xl text-[#2D323A] mb-0.5">{s.value}</p>
              <p className="text-sm text-[#717182]">{s.label}</p>
              <p className={`text-xs mt-1 flex items-center gap-0.5 ${s.trend?.color || 'text-[#717182]'}`}>
                {TrendIcon && <TrendIcon className="w-3 h-3" />} {s.trend?.text || s.change}
              </p>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
          <h3 className="text-[#2D323A] mb-4">{t('dashboard.charts.monthlyViews')}</h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={monthlyViews}>
              <defs>
                <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1E6DFF" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#1E6DFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB' }} />
              <Area type="monotone" dataKey="views" stroke="#1E6DFF" strokeWidth={2} fill="url(#viewsGradient)" name="Views" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
          <h3 className="text-[#2D323A] mb-4">{t('dashboard.charts.monthlyLeads')}</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={monthlyLeads}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#717182' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E5E7EB' }} />
              <Bar dataKey="leads" fill="#1E6DFF" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Complaints Widget */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
            <h3 className="text-[#2D323A]">{t('dashboard.complaints.title')}</h3>
          </div>
          <Link to="/dashboard/complaints" className="text-sm text-[#1E6DFF] hover:underline">
            {t('dashboard.complaints.viewAll')}
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          {[
            { label: t('dashboard.complaints.open'), value: openComplaints, color: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/10' },
            { label: t('dashboard.complaints.pendingResponse'), value: pendingComplaints, color: 'text-[#8B5CF6]', bg: 'bg-[#8B5CF6]/10' },
            { label: t('dashboard.complaints.resolved'), value: resolvedComplaints, color: 'text-[#22C55E]', bg: 'bg-[#22C55E]/10' },
          ].map((s) => (
            <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
              <p className={`text-xl ${s.color}`}>{s.value}</p>
              <p className="text-xs text-[#717182]">{s.label}</p>
            </div>
          ))}
        </div>
        <div className={`rounded-xl px-4 py-3 flex items-center justify-between ${urgentComplaint ? 'bg-[#EF4444]/5 border border-[#EF4444]/20' : 'bg-[#F5F7FA] border border-[#E5E7EB]'}`}>
          <div className="flex items-center gap-2">
            <AlertCircle className={`w-4 h-4 ${urgentComplaint ? 'text-[#EF4444]' : 'text-[#717182]'}`} />
            <span className={`text-sm ${urgentComplaint ? 'text-[#EF4444]' : 'text-[#717182]'}`}>
              {urgentComplaint ? t('dashboard.complaints.urgent') : t('dashboard.complaints.noUrgent')}
            </span>
          </div>
          {urgentComplaint && (
            <Link to={`/dashboard/complaints/${(urgentComplaint as any).id}`} className="flex items-center gap-1 text-sm text-[#EF4444] hover:underline whitespace-nowrap">
              {t('dashboard.complaints.view')} <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>

      {/* Bottom Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* My Listings */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB]">
          <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
            <h3 className="text-[#2D323A]">{t('dashboard.listings.title')}</h3>
            <Link to="/dashboard/listings" className="text-sm text-[#1E6DFF] hover:underline">
              {t('dashboard.listings.viewAll')}
            </Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {recentListings.length === 0 && <p className="p-4 text-sm text-[#717182]">{t('dashboard.listings.noListings')}</p>}
            {recentListings.slice(0, 3).map((vehicle) => (
              <div key={vehicle.id} className="flex items-center gap-3 p-4">
                <img src={getImageUrl(vehicle)} alt={vehicle.Title || 'Vehicle listing'} className="w-16 h-12 rounded-xl object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#2D323A] truncate">{vehicle.Title || vehicle.Category || 'Vehicle listing'}</p>
                  <p className="text-xs text-[#717182]">{vehicle.Price ? `${Number(vehicle.Price).toLocaleString()} MAD` : t('dashboard.listings.priceOnRequest')}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-[#717182]"><Eye className="w-3 h-3 inline mr-0.5" />{vehicle.ViewCount || 0}</span>
                    <span className="text-xs text-[#717182]"><Users className="w-3 h-3 inline mr-0.5" />{vehicle.leads || 0} {t('dashboard.listings.leads')}</span>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-lg ${vehicle.IsActive ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#F59E0B]/10 text-[#F59E0B]'}`}>
                  {vehicle.IsActive ? t('dashboard.listings.active') : t('dashboard.listings.inactive')}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Leads */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB]">
          <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
            <h3 className="text-[#2D323A]">{t('dashboard.leads.title')}</h3>
            <Link to="/dashboard/leads" className="text-sm text-[#1E6DFF] hover:underline">
              {t('dashboard.leads.viewAll')}
            </Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {recentLeads.length === 0 && <p className="p-4 text-sm text-[#717182]">{t('dashboard.leads.noLeads')}</p>}
            {recentLeads.slice(0, 3).map((lead) => (
              <div key={lead.id} className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 bg-[#1E6DFF]/10 rounded-xl flex items-center justify-center text-[#1E6DFF] flex-shrink-0 font-medium">
                  {(lead.BuyerName || t('dashboard.leads.buyer'))[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#2D323A]">{lead.BuyerName || t('dashboard.leads.buyer')}</p>
                  <p className="text-xs text-[#717182] truncate">{lead.Vehicle?.Title || 'Vehicle listing'}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs px-2 py-0.5 bg-[#F5F7FA] text-[#717182] rounded-lg block mb-1">{lead.LeadSource || t('dashboard.leads.enquiry')}</span>
                  <span className="text-xs text-[#BFC2C7]">{relativeTime(lead.CreatedAt, t)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}